<template>
  <Content
    class="route-builder phase-2 no-scroll"
    header-icon="map-marked-alt"
    :header-label="t('routeBuilder.routeBuilder')"
    :loading="isAnyLoading"
    :class="{
      blocked: !selectedClient,
      'has-error': request.currentStep === 'validation_error',
    }"
    full-width
  >
    <BlockUI :blocked="isScaffoldingRoutePlan" full-screen />
    <Splitter
      ref="verticalSplitter"
      layout="vertical"
      :gutter-size="SPLITTER_GUTTER_SIZE"
      @resizeend="onManualVerticalResize"
    >
      <SplitterPanel
        :size="panelSizes.height.firstRow"
        class="view-route-panel"
      >
        <ViewRouteSection
          id="view-route"
          v-model="viewRouteSectionSize"
          :proposed-shipments="proposedShipments"
          :mileages="mileages"
          :highlighted-shipment-identifiers="highlightedShipmentIdentifiers"
          @collapse="onViewRouteSectionCollapse"
          @expand="onViewRouteSectionExpand"
          @reset="onViewRouteSectionReset"
        />
      </SplitterPanel>

      <SplitterPanel :size="panelSizes.height.secondRow">
        <Transition name="initial-bottom-row">
          <Splitter v-if="isOnPlanningStep" :gutter-size="SPLITTER_GUTTER_SIZE">
            <SplitterPanel
              :size="panelSizes.width.firstColumn"
              class="planning-pool-panel"
            >
              <PlanningPoolSection
                id="planning-pool"
                :ship-orders="planningPool"
                :proposed-shipments="proposedShipments"
                :additional-ship-orders="additionalShipOrders"
                :ship-order-errors="shipOrderErrors"
                :readonly="isReadOnly"
                @add-to-planning-pool="addShipOrderToPlanningPool"
                @add-to-shipment="addShipOrderToShipment"
                @create-shipment="createShipmentFromShipOrders"
                @remove-from-planning-pool="removeShipOrdersFromPlanningPool"
                @remove-from-shipment="removeShipOrderFromShipment"
                @scaffold="onScaffoldRoutePlan"
                @split-ship-order="splitShipOrder"
              />
            </SplitterPanel>

            <SplitterPanel
              :size="panelSizes.width.secondColumn"
              class="shipments-panel"
            >
              <Phase2ProposedShipmentSection
                id="shipments"
                ref="proposedShipmentSection"
                :shipments="proposedShipments"
                :mileages="mileages"
                :disabled="request.currentStep === 'validation_error'"
                :readonly="isReadOnly"
                :scaffolding="isScaffoldingRoutePlan"
                :saving-draft="isSavingDraft"
                @add-shipment="onAddProposedShipment"
                @add-stop="onAddStopToShipment"
                @clear="clearProposedShipments"
                @highlight-shipments="highlightedShipmentIdentifiers = $event"
                @rate="onRateProposedShipments"
                @rearrange-stops="onRearrangeStops"
                @remove-ship-order="removeShipOrderFromShipment"
                @remove-shipment="onRemoveProposedShipment"
                @remove-stop="removeStopFromShipment"
                @save-draft="saveDraft"
                @submit="onCreateProposedShipments"
                @update-appointment="onUpdateAppointment"
              />
            </SplitterPanel>
          </Splitter>
        </Transition>

        <Transition name="rate-shop-transition">
          <RouteBuilderPhase2RateShopSection
            v-if="request.currentStep === 'rating'"
            id="rate-shop"
            :request="request"
            :mileages="mileages"
            @back="onBackToPlanning"
            @select-group="selectRateGroup"
          />
        </Transition>

        <RouteBuilderPhase2ExecutedShipmentsSection
          v-if="request.currentStep === 'executing'"
          id="execution"
          :request="request"
          @back="onBack"
        />
      </SplitterPanel>
    </Splitter>

    <AddShipmentModal
      v-model:visible="isAddShipmentModalVisible"
      @confirm="onAddProposedShipmentConfirm"
    />

    <RearrangeStopsModal
      v-model:visible="rearrangeStopsModal.visible"
      :shipment="rearrangeStopsModal.shipment"
      @add-stop="onAddStopToShipment"
      @confirm="rearrangeStops"
    />

    <AddStopToShipmentModal
      v-model:visible="addStopModal.visible"
      :stop-type="addStopModal.stopType"
      :stop-selector="addStopModal.stopSelector"
      @confirm="onAddStopToShipmentConfirm"
    />

    <ChangeDomainModal
      v-model:visible="isChangeDomainModalVisible"
      :selected-domain="selectedClient"
      :selectable-domain-types="[DomainType.Client]"
      :domain-options="clients.data"
      :header="t('common.selectParam', { param: t('clients.clients') })"
      position="center"
      closable
      @change-domain="onClientChange"
    >
      <template v-if="!!selectedClient" #message>
        <Message severity="warn" icon="pi pi-exclamation-triangle">
          {{
            t('common.selectingClientWillResetParam', {
              param: t('routeBuilder.routePlan'),
            })
          }}
        </Message>
      </template>
    </ChangeDomainModal>
  </Content>
</template>

<script lang="ts" setup>
import { usePrimeVue } from 'primevue/config';
import type { PropType, Ref } from 'vue';
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  toRefs,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useScrollAnimations } from '@rygen/rygen-ui/composables';
import { Environment } from '@rygen/rygen-ui/env/Environment';
import { EnvironmentPropertiesLoader } from '@rygen/rygen-ui/env/EnvironmentPropertiesLoader';
import {
  DomainReference,
  DomainTree,
  DomainType,
  Optional,
} from '@rygen/rygen-ui/model';

import { useShipOrderValidations } from '@/composables/routeBuilder';
import { useRouteBuilderPhase2 } from '@/composables/routeBuilder/phase2';
import { ShipmentStopType } from '@/model';
import {
  RequestAddStopToShipmentPayload,
  RearrangeStopsPayload,
  RemoveShipmentPayload,
  StopSelector,
  ProposedShipmentStop,
  AddStopPayload,
} from '@/model/routeBuilder';
import {
  RouteBuilderPhase2Request,
  RoutePlanProposedShipment,
  RoutePlanSelectRateGroupPayload,
} from '@/model/routeBuilder/phase2';
import { useClientStore } from '@/stores/Client';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RearrangeStopsModal from '@/components/multistop/RearrangeStopsModal.vue';
import AddShipmentModal from '@/components/routeBuilder/AddShipmentModal.vue';
import AddStopToShipmentModal from '@/components/routeBuilder/addStopToShipment/AddStopToShipmentModal.vue';
import PlanningPoolSection from '@/components/routeBuilder/planningPool/PlanningPoolSection.vue';
import Phase2ProposedShipmentSection from '@/components/routeBuilder/proposedShipment/Phase2ProposedShipmentSection.vue';
import RouteBuilderPhase2RateShopSection from '@/components/routeBuilder/rateShop/RouteBuilderPhase2RateShopSection.vue';
import RouteBuilderPhase2ExecutedShipmentsSection from '@/components/routeBuilder/RouteBuilderPhase2ExecutedShipmentsSection.vue';
import ViewRouteSection, {
  ViewRouteSectionSize,
} from '@/components/routeBuilder/ViewRouteSection.vue';
import ClientAvatar from '@rygen/rygen-ui/components/client/ClientAvatar.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';
import ChangeDomainModal from '@rygen/rygen-ui/components/domain/ChangeDomainModal.vue';
import BlockUI from 'primevue/blockui';
import Message from 'primevue/message';
import Splitter, { SplitterResizeEndEvent } from 'primevue/splitter';
import SplitterPanel from 'primevue/splitterpanel';

const ROUTE_BUILDER_PHASE_MESSAGE_KEY = 'route-builder-phase';
const OWNING_CLIENT_MESSAGE_KEY = 'route-builder-owning-client-message';

const VIEW_ROUTE_SECTION_DEFAULT_SIZES: Readonly<
  Record<ViewRouteSectionSize, number>
> = Object.freeze({
  collapsed: 15,
  normal: 33,
  expanded: 100,
});

/** Size of the `Splitter`'s divider in pixels */
const SPLITTER_GUTTER_SIZE = 2;

const ENVIRONMENTS_FOR_VERSION_BANNER = [
  Environment.LOCAL,
  Environment.DEV,
  Environment.QA,
];

const props = defineProps({
  shipmentIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  orderIds: {
    type: Array as PropType<number[]>,
    default: () => [],
  },
  routePlanId: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
});
const { shipmentIds, orderIds, routePlanId } = toRefs(props);

const proposedShipmentSection = useTemplateRef('proposedShipmentSection');
const verticalSplitter = useTemplateRef('verticalSplitter');

const { clientTreeMap, clients, loadClients } = useClientStore();
const { selectedDomain } = useCurrentUserStore();
const { t } = useI18n();
const { addMessage, removeMessage } = useMessageStore();
const { clearParentEntity, setParentEntity } = useParentEntityStore();
const {
  addProposedShipment,
  addShipOrderToPlanningPool,
  addShipOrderToShipment,
  addStopToShipment,
  additionalShipOrders,
  clearProposedShipments,
  createShipmentFromShipOrders,
  isOnPlanningStep,
  isReadOnly,
  isScaffoldingRoutePlan,
  loadDraftIntoBuilder,
  loadRelatedShipOrdersForOrders,
  loadRelatedShipOrdersForShipments,
  mileages,
  onScaffoldRoutePlan,
  onUpdateAppointment,
  planningPool,
  proposedShipments,
  rearrangeStops,
  removeShipOrderFromShipment,
  removeShipOrdersFromPlanningPool,
  removeShipment,
  removeStopFromShipment,
  request,
  reset,
  saveDraft,
  isSavingDraft,
  splitShipOrder,
} = useRouteBuilderPhase2();
const router = useRouter();
const { scrollIntoView } = useScrollAnimations();
const { errors: shipOrderErrors, validate: validateShipOrders } =
  useShipOrderValidations(
    () => planningPool.value.shipOrders,
    proposedShipments,
  );

const selectedClient: Ref<DomainTree | undefined> = ref();
const isChangeDomainModalVisible = ref(false);

const viewRouteSectionSize: Ref<ViewRouteSectionSize> = ref('normal');
const isAddShipmentModalVisible = ref(false);

const highlightedShipmentIdentifiers: Ref<
  Array<RoutePlanProposedShipment['identifier']>
> = ref([]);

const rearrangeStopsModal = ref({
  visible: false,
  shipment: new RoutePlanProposedShipment(),
});

const addStopModal = ref({
  visible: false,
  shipment: new RoutePlanProposedShipment(),
  stopType: undefined as Optional<ShipmentStopType>,
  stopSelector: undefined as Optional<StopSelector>,
  sequenceNumber: undefined as Optional<number>,
});

const panelSizes = ref({
  height: {
    firstRow: VIEW_ROUTE_SECTION_DEFAULT_SIZES.normal,
    secondRow: 100 - VIEW_ROUTE_SECTION_DEFAULT_SIZES.normal,
  },
  width: {
    firstColumn: 50,
    secondColumn: 50,
  },
});

const shouldShowVersionBanner = computed(() => {
  const effectiveEnvironment =
    EnvironmentPropertiesLoader.getEffectiveEnvironment(
      window.location.hostname,
    );

  return ENVIRONMENTS_FOR_VERSION_BANNER.includes(effectiveEnvironment);
});

const isAnyLoading = computed(() => {
  return planningPool.value.loading || additionalShipOrders.value.loading;
});

const onAddStopToShipment = ({
  stopType,
  stopSelector,
  sequenceNumber,
  shipmentIdentifier,
}: RequestAddStopToShipmentPayload) => {
  const { proposedShipment } =
    request.value.findProposedShipment(shipmentIdentifier);

  addStopModal.value.shipment = proposedShipment;
  addStopModal.value.stopType = stopType;
  addStopModal.value.stopSelector = stopSelector;
  addStopModal.value.sequenceNumber = sequenceNumber;
  addStopModal.value.visible = true;
};

const onAddStopToShipmentConfirm = (payload: AddStopPayload) => {
  const shipmentIdentifier = addStopModal.value.shipment.shipmentIdentifier;
  const sequenceNumber = addStopModal.value.sequenceNumber;
  addStopToShipment({
    ...payload,
    sequenceNumber,
    shipmentIdentifier,
  });
};

const onRearrangeStops = ({ shipmentIdentifier }: RearrangeStopsPayload) => {
  const { proposedShipment } =
    request.value.findProposedShipment(shipmentIdentifier);
  rearrangeStopsModal.value.shipment = proposedShipment;
  rearrangeStopsModal.value.visible = true;
};

const onRemoveProposedShipment = ({
  shipmentIdentifiers,
}: RemoveShipmentPayload) => {
  shipmentIdentifiers.forEach((identifier) => removeShipment(identifier));
};

const onViewRouteSectionCollapse = () => {
  updateRowSizes(VIEW_ROUTE_SECTION_DEFAULT_SIZES.collapsed);
};

const onViewRouteSectionExpand = () => {
  updateRowSizes(VIEW_ROUTE_SECTION_DEFAULT_SIZES.expanded);
};

const onViewRouteSectionReset = () => {
  updateRowSizes(VIEW_ROUTE_SECTION_DEFAULT_SIZES.normal);
};

const updateRowSizes = (newFirstRowHeight: number) => {
  panelSizes.value.height.firstRow = newFirstRowHeight;
  panelSizes.value.height.secondRow = 100 - newFirstRowHeight;

  nextTick(() => verticalSplitter.value?.resetState());
};

const isWithinThreshold = (size: ViewRouteSectionSize, actualSize: number) => {
  const sizeAmount = VIEW_ROUTE_SECTION_DEFAULT_SIZES[size];
  const minAmount = sizeAmount - 10;
  const maxAmount = sizeAmount + 10;

  return actualSize >= minAmount && actualSize <= maxAmount;
};

const onManualVerticalResize = ({
  sizes: [firstRowSize],
}: SplitterResizeEndEvent) => {
  if (isWithinThreshold('collapsed', firstRowSize)) {
    viewRouteSectionSize.value = 'collapsed';
  } else if (isWithinThreshold('expanded', firstRowSize)) {
    viewRouteSectionSize.value = 'expanded';
  } else {
    viewRouteSectionSize.value = 'normal';
  }

  nextTick(() => updateRowSizes(firstRowSize));
};

const onAddProposedShipment = () => {
  isAddShipmentModalVisible.value = true;
};

const onAddProposedShipmentConfirm = (stops: ProposedShipmentStop[]) => {
  addProposedShipment(stops);
};

const validateProposedShipments = async (): Promise<boolean> => {
  const areProposedShipmentsValid =
    await proposedShipmentSection.value?.validate();
  return areProposedShipmentsValid ?? false;
};

const validateManualConditions = async (): Promise<boolean> => {
  // Don't chain these calls, `one && two`, so a failure in one doesn't prevent
  // successive validations from running
  const areShipOrdersValid = await validateShipOrders();
  const areProposedShipmentsValid = await validateProposedShipments();
  return areShipOrdersValid && areProposedShipmentsValid;
};

const validate = async (): Promise<boolean> => {
  // const areAutoValidationsValid = await validate();
  const areManualValidationsValid = await validateManualConditions();

  if (!areManualValidationsValid) {
    nextTick(() => {
      const firstShipmentWithError = document.querySelector<HTMLElement>(
        `.proposed-shipment-id.phase-2.has-error`,
      );
      scrollIntoView(firstShipmentWithError);

      const firstShipOrderWithError = document.querySelector<HTMLElement>(
        `.planning-pool-ship-order-id.has-error`,
      );
      scrollIntoView(firstShipOrderWithError);
    });
  }

  return areManualValidationsValid;
};

const selectRateGroup = (payload: RoutePlanSelectRateGroupPayload) => {
  // Advancing to the executing step mounts RouteBuilderPhase2ExecutedShipmentsSection,
  // which performs the commit — the network call is intentionally tied to that mount.
  request.value.moveToExecuting(payload);
};

const onCreateProposedShipments = async () => {
  const isValid = await validate();
  if (!isValid) {
    request.value.setValidationError();
    return;
  }

  request.value.moveToExecuting();
};

const onRateProposedShipments = async () => {
  const isValid = await validate();
  request.value.moveToRating(isValid);
};

const onBack = () => {
  request.value.rateQuoteGroupType === 'none'
    ? onBackToPlanning()
    : onBackToRating();
};

const onBackToPlanning = () => {
  request.value.backToPlanning();
};

const onBackToRating = () => {
  request.value.backToRating();
};

const onClientChange = async (newClient: DomainReference) => {
  selectedClient.value = newClient.id
    ? clientTreeMap.value.get(newClient.id)
    : undefined;
  reset();

  // Clear the orderId/shipmentId query params
  const query = { ...router.currentRoute.value.query };
  delete query.orderId;
  delete query.shipmentId;

  await router.replace({ query });
};

const updateSelectedClient = (currentDomain: DomainReference | undefined) => {
  // Client should default to owner of selected orders/shipments
  let clientId = planningPool.value.owner?.id;

  // Fallback to the selected domain if its a client
  if (!clientId && currentDomain?.domainType === DomainType.Client) {
    clientId = currentDomain.id;
  }

  selectedClient.value = clientId
    ? clientTreeMap.value.get(clientId)
    : undefined;

  // Otherwise, make the user select a client
  isChangeDomainModalVisible.value = !selectedClient.value;
};

watch(orderIds, async (newVal: number[]) => {
  if (newVal.length) {
    loadRelatedShipOrdersForOrders(planningPool.value, newVal);
  }
});

watch(shipmentIds, async (newVal: number[]) => {
  if (newVal.length) {
    loadRelatedShipOrdersForShipments(planningPool.value, newVal);
  }
});

watch(
  () => planningPool.value.shipOrders,
  () => {
    if (request.value.currentStep === 'validation_error') {
      request.value.backToPlanning();
      shipOrderErrors.value = {};
    }
  },
  { deep: true },
);

watch(
  proposedShipments,
  () => {
    if (request.value.currentStep === 'validation_error') {
      request.value.backToPlanning();
    }
  },
  { deep: true },
);

watch(selectedDomain, (newVal: DomainReference | undefined) => {
  updateSelectedClient(newVal);
});

watch(
  selectedClient,
  (newVal: DomainTree | undefined) => {
    if (!newVal) {
      return;
    }

    // When reopening a saved plan by id, the loaded draft owns `request`; don't
    // reset it just because the client got resolved from the plan's owner.
    if (!routePlanId?.value) {
      request.value = new RouteBuilderPhase2Request({
        owner: newVal.asDomainReference(),
      });
    }
    setParentEntity(request);

    if (shouldShowVersionBanner.value) {
      addMessage({
        id: ROUTE_BUILDER_PHASE_MESSAGE_KEY,
        severity: 'info',
        text: 'Route Builder Phase 2',
      });
    }

    addMessage({
      id: OWNING_CLIENT_MESSAGE_KEY,
      severity: 'info',
      text: t('common.assignedToParam', { param: newVal.name }),
      iconSlot: () =>
        h(ClientAvatar, {
          name: newVal.name,
          logo: newVal.branding?.avatar?.signedUrl,
        }),
      onClick: () => (isChangeDomainModalVisible.value = true),
    });
  },
  { immediate: true },
);

watch(isChangeDomainModalVisible, (newVal: boolean, oldVal: boolean) => {
  if (oldVal && !newVal && !selectedClient.value) {
    router.back();
  }
});

onMounted(async () => {
  // Reopen an existing plan by id: load its shipments, then resolve the client
  // from the plan's owner so we don't prompt the user to pick one.
  const reopenPlanId = routePlanId?.value;
  if (reopenPlanId) {
    await loadClients();
    const discarded = await loadDraftIntoBuilder(reopenPlanId);
    if (discarded) {
      router.push({ name: 'routePlanList' });
      return;
    }
    const ownerId =
      request.value.proposedShipments[0]?.owner?.id ?? request.value.owner?.id;
    selectedClient.value = ownerId
      ? clientTreeMap.value.get(ownerId)
      : undefined;
    return;
  }

  const initializationPromises = [];
  if (orderIds.value.length) {
    initializationPromises.push(
      loadRelatedShipOrdersForOrders(planningPool.value, orderIds.value),
    );
  } else if (shipmentIds.value.length) {
    initializationPromises.push(
      loadRelatedShipOrdersForShipments(planningPool.value, shipmentIds.value),
    );
  }

  initializationPromises.push(loadClients());
  await Promise.allSettled(initializationPromises);
  updateSelectedClient(selectedDomain.value);
});

onBeforeUnmount(() => {
  clearParentEntity();
  removeMessage(ROUTE_BUILDER_PHASE_MESSAGE_KEY);
  removeMessage(OWNING_CLIENT_MESSAGE_KEY);
});

defineExpose({
  $primevue: usePrimeVue(),
});
</script>
